import { MenuItem, Select, Tooltip } from "@mui/material";
import PropTypes from "prop-types";
import React from "react";

/** Layout split options shared by jigsaw + sliding play toolbars. */
export const PLAY_LAYOUT_SPLIT_OPTIONS = [
    { key: "4-8", label: "1/3" },
    { key: "6-6", label: "3/3" },
    { key: "hide", label: "100%" },
];

const SELECT_SX = {
    flexShrink: 0,
    minWidth: 76,
    height: 32,
    typography: "caption",
    bgcolor: "background.paper",
    "& .MuiSelect-select": { py: 0.5, px: 1.25 },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "divider" },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#3BA666" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "#3BA666",
        borderWidth: 2,
    },
};

/**
 * Compact dropdown for reference/puzzle layout (1/3 = 1:3 ref:grid · 3/3 = 1:1 · 100%).
 * {@code value} is {@code 4-8} | {@code 6-6} | {@code hide}.
 */
export default function PlayLayoutSplitSelect({ value, onChange, ariaLabel, tooltipTitle, sx, options }) {
    const splitOptions = options ?? PLAY_LAYOUT_SPLIT_OPTIONS;
    const select = (
        <Select
            size="small"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            aria-label={ariaLabel}
            data-play-toolbar-no-drag
            MenuProps={{ disableScrollLock: true }}
            sx={{ ...SELECT_SX, ...sx }}
        >
            {splitOptions.map((p) => (
                <MenuItem key={p.key} value={p.key} sx={{ typography: "caption" }}>
                    {p.label}
                </MenuItem>
            ))}
        </Select>
    );
    const stopToolbarDrag = (e) => {
        e.stopPropagation();
    };
    const wrap = (node) => (
        <span
            style={{ display: "inline-flex" }}
            data-play-toolbar-no-drag
            onPointerDown={stopToolbarDrag}
            onClick={stopToolbarDrag}
        >
            {node}
        </span>
    );
    if (!tooltipTitle) {
        return wrap(select);
    }
    return (
        <Tooltip title={tooltipTitle} arrow>
            {wrap(select)}
        </Tooltip>
    );
}

PlayLayoutSplitSelect.propTypes = {
    value: PropTypes.oneOf(["4-8", "6-6", "hide"]).isRequired,
    onChange: PropTypes.func.isRequired,
    ariaLabel: PropTypes.string,
    tooltipTitle: PropTypes.string,
    sx: PropTypes.object,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.oneOf(["4-8", "6-6", "hide"]).isRequired,
            label: PropTypes.string.isRequired,
        }),
    ),
};

/** Jigsaw {@code layoutMode} ↔ split keys used by this select. */
export function jigsawLayoutModeToSplitKey(layoutMode) {
    if (layoutMode === "hide") return "hide";
    if (layoutMode === "narrow") return "4-8";
    return "6-6";
}

export function jigsawSplitKeyToLayoutMode(splitKey) {
    if (splitKey === "hide") return "hide";
    if (splitKey === "4-8") return "narrow";
    return "split";
}
